import { formatValidQueryParamUrlCommaSeparated } from "utils/common";
import { createPaginatedEndpoint } from "utils/services.util";

import { emptySplitApi } from "./empty-split-api";
import type {
  InsightCategory,
  InsightTag,
  ScribeInsight,
  ScribeInsightFacets,
  ScribeInsightsFilters,
  ScribeInsightsResponse
} from "./types/scribe-insight.type";

/**
 * Backend `default_limit` is 5, so always pass an explicit limit for a feed.
 * `max_limit` is 300.
 */
export const DEFAULT_INSIGHTS_LIMIT = 15;

export const scribeInsightsApi = emptySplitApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    /**
     * Patient insights feed. Built with `createPaginatedEndpoint` so it plugs
     * into `usePaginationV2`: pages are page-keyed and merged in cache, and
     * `serializeQueryArgs` drops offset/limit from the cache key — so changing
     * a filter (category/sentiment/tags/…) starts a fresh page-0 fetch.
     */
    getScribeInsightsFeed: createPaginatedEndpoint<
      ScribeInsightsFilters & { offset?: number; limit?: number },
      ScribeInsight[]
    >(builder, {
      queryFn: ({ offset = 0, limit = DEFAULT_INSIGHTS_LIMIT, ...filters }) => {
        const filtersString = formatValidQueryParamUrlCommaSeparated(filters);
        return `/ai/scribe-insights/?limit=${limit}&offset=${offset}&${filtersString}`;
      },
      transformResponseFn: (response: ScribeInsightsResponse) =>
        response.results,
      providesTags: ["ScribeInsights"]
    }),

    /**
     * Discrete-page variant of the feed for the table view. Unlike
     * `getScribeInsightsFeed` (merge-based, page-keyed — accumulates pages for
     * infinite scroll), this is a plain query cached by full args (offset/limit
     * included), so each page replaces the last — the shape DataGridPremium's
     * server pagination expects. Shares the `ScribeInsights` tag, so
     * pin/dismiss mutations invalidate it too.
     */
    getScribeInsightsPage: builder.query<
      ScribeInsight[],
      ScribeInsightsFilters & { offset?: number; limit?: number }
    >({
      query: ({ offset = 0, limit = DEFAULT_INSIGHTS_LIMIT, ...filters }) => {
        const filtersString = formatValidQueryParamUrlCommaSeparated(filters);
        return `/ai/scribe-insights/?limit=${limit}&offset=${offset}&${filtersString}`;
      },
      transformResponse: (response: ScribeInsightsResponse) => response.results,
      providesTags: ["ScribeInsights"]
    }),

    pinInsight: builder.mutation<ScribeInsight, number>({
      query: (id) => ({
        url: `/ai/scribe-insights/${id}/pin/`,
        method: "POST"
      }),
      invalidatesTags: ["ScribeInsights"]
    }),

    unpinInsight: builder.mutation<ScribeInsight, number>({
      query: (id) => ({
        url: `/ai/scribe-insights/${id}/unpin/`,
        method: "POST"
      }),
      invalidatesTags: ["ScribeInsights"]
    }),

    dismissInsight: builder.mutation<ScribeInsight, number>({
      query: (id) => ({
        url: `/ai/scribe-insights/${id}/dismiss/`,
        method: "POST"
      }),
      invalidatesTags: ["ScribeInsights"]
    }),

    undismissInsight: builder.mutation<ScribeInsight, number>({
      query: (id) => ({
        url: `/ai/scribe-insights/${id}/undismiss/`,
        method: "POST"
      }),
      invalidatesTags: ["ScribeInsights"]
    }),

    /**
     * Per-category counts + total for the patient's insights. Shares the
     * `ScribeInsights` tag (not a dedicated one) so pin/dismiss mutations
     * invalidate it too — this is called both from the insights feed (chip
     * counts) and from the patient header (side-menu total badge), and both
     * need to stay fresh after every triage action.
     */
    getScribeInsightsFacets: builder.query<
      ScribeInsightFacets,
      { patient: number | undefined; is_key_insight?: boolean }
    >({
      query: (filters) => {
        const filtersString = formatValidQueryParamUrlCommaSeparated(filters);
        return `/ai/scribe-insights/facets/?${filtersString}`;
      },
      providesTags: ["ScribeInsights"]
    }),

    getInsightCategories: builder.query<InsightCategory[], void>({
      query: () => "/ai/insight-categories/",
      providesTags: ["InsightCategories"]
    }),

    getInsightTags: builder.query<InsightTag[], void>({
      query: () => "/ai/insight-tags/",
      providesTags: ["InsightTags"]
    })
  })
});

export const {
  useGetScribeInsightsFeedQuery,
  useGetScribeInsightsPageQuery,
  usePinInsightMutation,
  useUnpinInsightMutation,
  useDismissInsightMutation,
  useUndismissInsightMutation,
  useGetScribeInsightsFacetsQuery,
  useGetInsightCategoriesQuery,
  useGetInsightTagsQuery
} = scribeInsightsApi;
